import { FileText, Download } from "lucide-react";
import { API } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { Field } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { KINDS, StatusBadge, DaysLeft, dueLabel } from "@/components/documents/docConst";

const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export const DocumentSheet = ({ doc, owners, canWrite, onClose, onEdit, onRenew, onDelete }) => {
  const { nameOf } = useData();
  const versions = [...(doc?.versions || [])].reverse();
  return (
    <Sheet open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="document-sheet">
        {doc && <>
          <SheetHeader><SheetTitle className="font-display text-2xl">{doc.title}</SheetTitle></SheetHeader>
          <div className="mt-2 flex items-center gap-2"><span className="micro-label">{KINDS[doc.kind]}</span><StatusBadge d={doc} testId="document-sheet-status" /></div>
          <div className="mt-5 grid grid-cols-2 gap-4">
            <Field label="Società" value={doc.company_id ? nameOf("companies", doc.company_id, "legal_name") : "Tutto il gruppo"} />
            <Field label={doc.kind === "certificazione" ? "Ente" : doc.kind === "procedura" ? "Ambito" : "Controparte"} value={doc.counterparty || "—"} />
            <Field label="Numero / versione" value={doc.number || "—"} mono />
            <Field label="Emissione" value={doc.issue_date ? fmtDate(doc.issue_date) : "—"} mono />
            <div><div className="micro-label">{dueLabel(doc.kind)}</div><div className="mt-1"><DaysLeft d={doc} /></div></div>
            <Field label="Responsabile" value={owners.find((u) => u.id === doc.owner_user_id)?.name || "Solo amministratori"} />
          </div>
          {doc.notes && <p className="mt-4 whitespace-pre-wrap border-l-2 pl-3 text-sm text-muted-foreground">{doc.notes}</p>}
          {canWrite && <div className="mt-5 flex flex-wrap gap-2">
            <Button size="sm" onClick={onRenew} data-testid="document-renew-button">Rinnova / nuova versione</Button>
            <Button size="sm" variant="outline" onClick={onEdit} data-testid="document-edit-button">Modifica dati</Button>
            <Button size="sm" variant="ghost" className="text-red-700" onClick={onDelete} data-testid="document-delete-button">Elimina</Button>
          </div>}
          <div className="micro-label mb-2 mt-8 border-b pb-1">Storico versioni ({versions.length})</div>
          {versions.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="document-no-versions">Nessun file caricato.</p> : versions.map((v, i) => (
            <div key={v.id} className="flex items-center gap-3 border-b py-2.5 text-sm" data-testid={`document-version-${v.id}`}>
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold">v{v.version} · {v.filename}{i === 0 && <span className="ml-2 bg-emerald-50 px-1.5 text-[10px] uppercase text-emerald-800">Attuale</span>}</div>
                <div className="text-xs text-muted-foreground">{kb(v.size)} · {v.uploaded_by} · {fmtDateTime(v.created_at)}{v.due_date && ` · ${dueLabel(doc.kind).toLowerCase()} ${fmtDate(v.due_date)}`}</div>
              </div>
              <a href={`${API}/documents/${doc.id}/versions/${v.id}/file`} target="_blank" rel="noreferrer" data-testid={`document-version-download-${v.id}`}><Button size="icon" variant="ghost"><Download className="h-4 w-4" /></Button></a>
            </div>
          ))}
        </>}
      </SheetContent>
    </Sheet>
  );
};
